"use client";

import React, { useMemo, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Banknote,
  Bath,
  Bed,
  Box,
  Building,
  Calendar,
  Camera,
  Car,
  Check,
  ChevronDown,
  Construction,
  Dog,
  Fence,
  Globe2,
  Info,
  Landmark,
  Map,
  Sparkles,
  Trees,
} from "lucide-react";
import { twMerge } from "tailwind-merge";
import { furnishingTypes } from "@/db/definitions";
import {
  conditionTypes,
  parkingTypes,
  petsAllowedTypes,
} from "@/lib/data/attributes";
import {
  getAllLocationCityOptions,
  getAllProvinceOptions,
  getCityOptionsByProvince,
} from "@/lib/data/location/data";
import {
  getHistogramFiltersUrlQuery,
  getMinimumEnergyLabel,
  type ListingFilters,
} from "@/lib/fetch/listing";
import type { HistogramListingResponse } from "@/lib/types/search";
import FurnishingInfo from "@/components/listing/FurnishingInfo";
import FilterPropertyType from "@/app/[locale]/(public)/search/_components/filter/FilterPropertyType";
import FilterCombobox from "@/app/[locale]/(public)/search/_components/filter/FilterCombobox";
import FilterRange from "@/app/[locale]/(public)/search/_components/filter/FilterRange";
import FilterHistogram from "@/app/[locale]/(public)/search/_components/filter/FilterHistogram";
import FilterChoice from "@/app/[locale]/(public)/search/_components/filter/FilterChoice";
import FilterEnergyLabel from "@/app/[locale]/(public)/search/_components/filter/FilterEnergyLabel";

type Props = {
  filters: ListingFilters;
  onChange: (filters: ListingFilters) => void;
};

const BEDROOM_STEPS = [1, 2, 3, 4, 5];
const BATHROOM_STEPS = [1, 2, 3];

/**
 * The listing filter body, shared by public search/map and the dashboard alert
 * editor. Lays out as one column in narrow panels and two columns in wide
 * containers (container queries).
 */
export default function ListingFilterFields({ filters, onChange }: Props) {
  const t = useTranslations("search");
  const tL = useTranslations("listing");
  const locale = useLocale();

  const set = (patch: Partial<ListingFilters>) =>
    onChange({ ...filters, ...patch, page: 1 });

  const provinces = useMemo(() => getAllProvinceOptions(locale), [locale]);
  const hasProvince = !!filters.province && filters.province !== "ANY";
  const cities = useMemo(
    () =>
      hasProvince
        ? getCityOptionsByProvince(filters.province!, locale)
        : getAllLocationCityOptions(locale),
    [hasProvince, filters.province, locale],
  );

  const histogramParamsString = useMemo(
    () => getHistogramFiltersUrlQuery(filters).params.toString(),
    [filters],
  );
  const { isFetching, data } = useQuery({
    queryKey: ["histogram-filters", histogramParamsString],
    queryFn: async () => {
      const response = await fetch(
        `/api/site/public/listing/histogram?${histogramParamsString}`,
      );
      if (!response.ok) return null;

      const json = (await response.json()) as HistogramListingResponse;
      return json.data;
    },
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const any = tL("attributes.any");
  const minSteps = (steps: number[]) =>
    steps.map((n) => ({ label: `${n}+`, value: String(n) }));
  const optionsOf = <T extends string>(
    values: readonly T[],
    label: (value: T) => string,
  ): { label: string; value: T | "ANY" }[] =>
    values.map((value) => ({ label: label(value), value }));

  const mustHaves = [
    { key: "atLeastOneImage", label: t("must-haves.photos"), icon: Camera },
    { key: "hasBalcony", label: t("must-haves.balcony"), icon: Fence },
    { key: "hasGarden", label: t("must-haves.garden"), icon: Trees },
  ] as const;

  const moreActive = [
    filters.volumeMin,
    filters.volumeMax,
    filters.floorsMin,
    filters.floorsMax,
    filters.gardenAreaMin,
    filters.gardenAreaMax,
    filters.yearMin,
    filters.yearMax,
  ].filter((value) => value !== undefined).length;
  const [moreOpen, setMoreOpen] = useState(moreActive > 0);

  return (
    <div className="@container flex w-full min-w-0 flex-col">
      <Section title={t("sections.home")}>
        <FilterPropertyType
          value={filters.type}
          setValue={(type) => set({ type })}
        />
        <div className="grid grid-cols-1 gap-6 @xl:grid-cols-2">
          <FilterCombobox
            icon={<Globe2 />}
            title={t("location.province.name")}
            value={filters.province}
            setValue={(province) => {
              const inProvince = province
                ? getCityOptionsByProvince(province, locale)
                : null;
              set({
                province,
                // Drop a city that is not in the newly chosen province.
                city:
                  inProvince &&
                  filters.city &&
                  !inProvince.some(
                    (city) =>
                      city.value.toLocaleLowerCase() ===
                      filters.city!.toLocaleLowerCase(),
                  )
                    ? undefined
                    : filters.city,
              });
            }}
            options={provinces}
            anyLabel={t("location.province.any")}
            searchPlaceholder={t("combobox.search-province")}
          />
          <FilterCombobox
            icon={<Landmark />}
            title={t("location.city.name")}
            value={filters.city}
            setValue={(city) => set({ city })}
            options={cities}
            anyLabel={t("location.city.any")}
            searchPlaceholder={t("combobox.search-city")}
          />
        </div>
      </Section>

      <Section title={t("sections.budget")}>
        <div className="grid grid-cols-1 gap-8 @xl:grid-cols-2 @xl:gap-6">
          <FilterRange
            icon={<Banknote />}
            title={tL("attribute_title.PRICE")}
            unit="€"
            min={filters.priceMin}
            max={filters.priceMax}
            setMin={(priceMin) => set({ priceMin })}
            setMax={(priceMax) => set({ priceMax })}
            clear={() => set({ priceMin: undefined, priceMax: undefined })}
          >
            <FilterHistogram
              buckets={data?.price}
              isLoading={isFetching}
              barCount={12}
              selectedMin={filters.priceMin ?? null}
              selectedMax={filters.priceMax ?? null}
              formatLabel={(b) => `€${b.min}–${b.max ?? "∞"}`}
              type="price"
              onBucketClick={(b) =>
                set({ priceMin: b.min || undefined, priceMax: b.max ?? undefined })
              }
              onRangeChange={(priceMin, priceMax) =>
                set({ priceMin: priceMin || undefined, priceMax })
              }
              thumbLabels={[
                `${tL("attribute_title.PRICE")}: ${t("input.minimum")}`,
                `${tL("attribute_title.PRICE")}: ${t("input.maximum")}`,
              ]}
            />
          </FilterRange>
          <FilterRange
            icon={<Map />}
            title={tL("attribute_title.SIZE")}
            unit="m²"
            min={filters.areaMin}
            max={filters.areaMax}
            setMin={(areaMin) => set({ areaMin })}
            setMax={(areaMax) => set({ areaMax })}
            clear={() => set({ areaMin: undefined, areaMax: undefined })}
          >
            <FilterHistogram
              buckets={data?.size}
              isLoading={isFetching}
              barCount={12}
              selectedMin={filters.areaMin ?? null}
              selectedMax={filters.areaMax ?? null}
              formatLabel={(b) => `${b.min}–${b.max ?? "∞"} m²`}
              type="size"
              onBucketClick={(b) =>
                set({ areaMin: b.min || undefined, areaMax: b.max ?? undefined })
              }
              onRangeChange={(areaMin, areaMax) =>
                set({ areaMin: areaMin || undefined, areaMax })
              }
              thumbLabels={[
                `${tL("attribute_title.SIZE")}: ${t("input.minimum")}`,
                `${tL("attribute_title.SIZE")}: ${t("input.maximum")}`,
              ]}
            />
          </FilterRange>
        </div>
      </Section>

      <Section title={t("sections.rooms")}>
        <div className="grid grid-cols-1 gap-6 @xl:grid-cols-2">
          <FilterChoice
            anyValue="ANY"
            anyLabel={any}
            icon={<Bed />}
            title={tL("attribute_title.BEDROOMS")}
            variant="segmented"
            value={
              filters.bedroomsMin !== undefined
                ? String(filters.bedroomsMin)
                : "ANY"
            }
            setValue={(value) => {
              const min = value === "ANY" ? undefined : Number(value);
              set({
                bedroomsMin: min,
                bedroomsMax:
                  min !== undefined &&
                  filters.bedroomsMax !== undefined &&
                  filters.bedroomsMax < min
                    ? undefined
                    : filters.bedroomsMax,
              });
            }}
            options={minSteps(BEDROOM_STEPS)}
          />
          <FilterChoice
            anyValue="ANY"
            anyLabel={any}
            icon={<Bath />}
            title={tL("attribute_title.BATHROOMS")}
            variant="segmented"
            value={
              filters.bathroomsMin !== undefined
                ? String(filters.bathroomsMin)
                : "ANY"
            }
            setValue={(value) => {
              const min = value === "ANY" ? undefined : Number(value);
              set({
                bathroomsMin: min,
                bathroomsMax:
                  min !== undefined &&
                  filters.bathroomsMax !== undefined &&
                  filters.bathroomsMax < min
                    ? undefined
                    : filters.bathroomsMax,
              });
            }}
            options={minSteps(BATHROOM_STEPS)}
          />
        </div>
      </Section>

      <Section
        title={t("sections.must-haves")}
        description={t("must-haves.hint")}
      >
        <div className="flex flex-wrap gap-2">
          {mustHaves.map(({ key, label, icon: Icon }) => {
            const checked = filters[key] === true;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={checked}
                onClick={() => set({ [key]: checked ? undefined : true })}
                className={twMerge(
                  "flex min-h-10 items-center gap-2 rounded-full border border-input bg-background px-4 text-sm font-medium transition-colors hover:border-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1",
                  checked &&
                    "border-primary bg-secondary font-semibold text-secondary-foreground hover:border-primary",
                )}
              >
                {checked ? (
                  <Check className="size-4" strokeWidth={2.5} aria-hidden />
                ) : (
                  <Icon className="size-4 text-muted-foreground" aria-hidden />
                )}
                {label}
              </button>
            );
          })}
        </div>
      </Section>

      <Section title={t("sections.details")}>
        <div className="grid grid-cols-1 gap-6 @xl:grid-cols-2">
          <FilterChoice
            anyValue="ANY"
            anyLabel={any}
            icon={<Sparkles />}
            title={tL("attribute_title.FURNISHING_TYPE")}
            value={filters.furnished ?? "ANY"}
            setValue={(furnished) => set({ furnished })}
            options={optionsOf(furnishingTypes, (v) =>
              tL(`attributes.FURNISHING_TYPE.${v}`),
            )}
            info={
              <FurnishingInfo
                highlight={
                  filters.furnished && filters.furnished !== "ANY"
                    ? filters.furnished
                    : undefined
                }
                highlightLabel="selected"
                trigger={
                  <button
                    type="button"
                    aria-label={tL("furnishing-info.open")}
                    className="-my-2 inline-flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted data-[state=open]:text-foreground"
                  >
                    <Info className="size-4" />
                  </button>
                }
              />
            }
          />
          <FilterEnergyLabel
            value={getMinimumEnergyLabel(filters.energyLabel)}
            setValue={(label) =>
              set({ energyLabel: label ? [label] : undefined })
            }
          />
          <FilterChoice
            anyValue="ANY"
            anyLabel={any}
            icon={<Dog />}
            title={tL("attribute_title.PETS_ALLOWED")}
            value={filters.petsAllowed ?? "ANY"}
            setValue={(petsAllowed) => set({ petsAllowed })}
            options={optionsOf(petsAllowedTypes, (v) =>
              tL(`attributes.PETS_ALLOWED.${v}`),
            )}
          />
          <FilterChoice
            anyValue="ANY"
            anyLabel={any}
            icon={<Car />}
            title={tL("attribute_title.PARKING_TYPE")}
            value={filters.parkingType ?? "ANY"}
            setValue={(parkingType) => set({ parkingType })}
            options={optionsOf(parkingTypes, (v) =>
              tL(`attributes.PARKING_TYPE.${v}`),
            )}
          />
          <FilterChoice
            anyValue="ANY"
            anyLabel={any}
            icon={<Construction />}
            title={tL("attribute_title.CONDITION")}
            value={filters.condition ?? "ANY"}
            setValue={(condition) => set({ condition })}
            options={optionsOf(conditionTypes, (v) =>
              tL(`attributes.CONDITION.${v}`),
            )}
          />
        </div>
      </Section>

      <div className="border-t border-border pt-4">
        <button
          type="button"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen((open) => !open)}
          className="flex min-h-11 w-full items-center justify-between gap-2 rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <span className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("sections.more")}
            </span>
            {moreActive > 0 && (
              <span className="rounded-full bg-secondary px-1.5 text-[11px] font-bold text-secondary-foreground">
                {moreActive}
              </span>
            )}
          </span>
          <ChevronDown
            className={twMerge(
              "size-4 text-muted-foreground transition-transform",
              moreOpen && "rotate-180",
            )}
          />
        </button>
        {moreOpen && (
          <div className="grid grid-cols-1 gap-6 pb-2 pt-4 @xl:grid-cols-2">
            <FilterRange
              icon={<Box />}
              title={tL("attribute_title.VOLUME")}
              unit="m³"
              min={filters.volumeMin}
              max={filters.volumeMax}
              setMin={(volumeMin) => set({ volumeMin })}
              setMax={(volumeMax) => set({ volumeMax })}
              clear={() => set({ volumeMin: undefined, volumeMax: undefined })}
            />
            <FilterRange
              icon={<Building />}
              title={tL("attribute_title.NUMBER_OF_FLOORS")}
              min={filters.floorsMin}
              max={filters.floorsMax}
              setMin={(floorsMin) => set({ floorsMin })}
              setMax={(floorsMax) => set({ floorsMax })}
              clear={() => set({ floorsMin: undefined, floorsMax: undefined })}
            />
            <FilterRange
              icon={<Trees />}
              title={tL("attribute_title.GARDEN_SIZE")}
              unit="m²"
              min={filters.gardenAreaMin}
              max={filters.gardenAreaMax}
              setMin={(gardenAreaMin) => set({ gardenAreaMin })}
              setMax={(gardenAreaMax) => set({ gardenAreaMax })}
              clear={() =>
                set({ gardenAreaMin: undefined, gardenAreaMax: undefined })
              }
            />
            <FilterRange
              icon={<Calendar />}
              title={tL("attribute_title.CONSTRUCTION_YEAR")}
              min={filters.yearMin}
              max={filters.yearMax}
              setMin={(yearMin) => set({ yearMin })}
              setMax={(yearMax) => set({ yearMax })}
              clear={() => set({ yearMin: undefined, yearMax: undefined })}
              placeholder={{
                min: "1900",
                max: String(new Date().getFullYear()),
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

type SectionProps = {
  title: string;
  description?: string;
  children: React.ReactNode;
};

const Section: React.FC<SectionProps> = ({ title, description, children }) => (
  <section className="flex flex-col gap-5 border-t border-border py-6 first:border-t-0 first:pt-0">
    <div className="flex flex-col gap-1">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
    </div>
    {children}
  </section>
);
